export interface UploadedAsset {
  asset_id: string;
  wizard: 'client' | 'talent' | 'partner' | 'advisor' | 'ambassador';
  step: string;
  field_name: string;
  filename: string;
  content_type: string;
  bytes: number;
  sha256: string;
  uploaded_at: string;
}

export interface PendingFilePlaceholder {
  __type: 'file-placeholder';
  name: string;
  size: number;
  content_type: string;
  pending_upload: true;
}

export function isUploadedAsset(value: unknown): value is UploadedAsset {
  return !!value && typeof value === 'object' && 'asset_id' in value && typeof (value as UploadedAsset).asset_id === 'string';
}

export function isPendingFilePlaceholder(value: unknown): value is PendingFilePlaceholder {
  return !!value && typeof value === 'object' && (value as PendingFilePlaceholder).__type === 'file-placeholder';
}

export function serialiseWizardValue<T>(value: T): T {
  if (typeof File !== 'undefined' && value instanceof File) {
    return {
      __type: 'file-placeholder',
      name: value.name,
      size: value.size,
      content_type: value.type,
      pending_upload: true,
    } as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => serialiseWizardValue(item)) as T;
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nested]) => [key, serialiseWizardValue(nested)]),
    ) as T;
  }
  return value;
}

export function fileValueLabel(value: unknown): string {
  if (typeof File !== 'undefined' && value instanceof File) return value.name;
  if (isUploadedAsset(value)) return `${value.filename} · uploaded`;
  if (isPendingFilePlaceholder(value)) return `${value.name} · reselect required`;
  if (typeof value === 'string') return value;
  return '';
}

export async function uploadOnboardingAsset({
  apiBase,
  token,
  wizard,
  step,
  fieldName,
  file,
}: {
  apiBase: string;
  token: string;
  wizard: 'client' | 'talent' | 'partner' | 'advisor' | 'ambassador';
  step: string;
  fieldName: string;
  file: File;
}): Promise<UploadedAsset> {
  const form = new FormData();
  form.append('wizard', wizard);
  form.append('step', step);
  form.append('field_name', fieldName);
  form.append('file', file);
  const response = await fetch(`${apiBase}/api/v1/onboarding/assets`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText} — ${await response.text()}`);
  return response.json() as Promise<UploadedAsset>;
}
